import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import {
  getUserSubscription,
  UserSubscription,
  SubscriptionTier,
  TIER_FEATURES
} from '../services/userService'

interface SubscriptionContextType {
  subscription: UserSubscription | null
  tier: SubscriptionTier
  loading: boolean
  isPro: boolean
  // Feature checks
  canAutoRefresh: boolean
  canAutoDelete: boolean
  canNuke: boolean
  maxAccounts: number
  allowedRetentionDays: readonly number[]
  // Refresh subscription data
  refreshSubscription: () => Promise<void>
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null)

export function useSubscription() {
  const context = useContext(SubscriptionContext)
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider')
  }
  return context
}

interface SubscriptionProviderProps {
  children: ReactNode
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { user } = useAuth()
  const [subscription, setSubscription] = useState<UserSubscription | null>(null)
  const [loading, setLoading] = useState(true)

  const loadSubscription = async () => {
    if (!user) {
      setSubscription(null)
      setLoading(false)
      return
    }

    try {
      const sub = await getUserSubscription(user.uid)
      setSubscription(sub)
    } catch (error) {
      console.error('Error loading subscription:', error)
      setSubscription({ tier: 'free' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSubscription()
  }, [user])

  const tier = subscription?.tier || 'free'
  const features = TIER_FEATURES[tier]

  const value: SubscriptionContextType = {
    subscription,
    tier,
    loading,
    isPro: tier === 'pro',
    canAutoRefresh: features.autoRefresh,
    canAutoDelete: features.autoDelete,
    canNuke: features.nuke,
    maxAccounts: features.maxAccounts,
    allowedRetentionDays: features.retentionDays,
    refreshSubscription: loadSubscription
  }

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}
